package diana.soleil.design.iraniancanadienbazzar.core.data.fake

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.model.AuthUser
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow

/**
 * The signed-in QA persona for the Developer Menu's mock-data mode.
 *
 * Why this exists: `mockDataModule` swapped every repository for a fake but left the REAL
 * [AuthService] in place, so mock mode ran with fake data and a live anonymous session. Everything
 * gated on `isGuest` therefore stayed locked — most visibly the Messages tab, which sends guests
 * straight to the sign-in screen. QA could populate the whole app and still not reach the inbox,
 * which is how the Messages status-bar bug survived to production. `CLAUDE.md` rule 9 asks that
 * features be exercisable without hacks; this is the missing half of that.
 *
 * Presents [MockScenarios.MOCK_UID] as a real (non-anonymous) account, which is the same identity
 * the fake repositories already use to resolve ownership and `isMine`.
 *
 * Debug-only: reachable solely through the Developer Menu, which is stripped from release builds.
 */
internal class FakeAuthService : AuthService {

    private val user = AuthUser(
        uid = MockScenarios.MOCK_UID,
        email = "${MockScenarios.MOCK_UID}@mahaleh.test",
        displayName = MockScenarios.MOCK_NAME,
        isAnonymous = false,
    )

    private val state = MutableStateFlow<AuthUser?>(user)

    override val authState: Flow<AuthUser?> = state
    override val currentUser: AuthUser? get() = state.value

    // Credentials are irrelevant here: mock mode is already "signed in", and a QA tap on any
    // sign-in path should land back on the persona rather than fail.
    override suspend fun signIn(email: String, password: String): DataResult<AuthUser> = signedIn()
    override suspend fun signUp(email: String, password: String): DataResult<AuthUser> = signedIn()
    override suspend fun signInAnonymously(): DataResult<AuthUser> = signedIn()
    override suspend fun signInWithGoogle(idToken: String, accessToken: String?): DataResult<AuthUser> = signedIn()
    override suspend fun signInWithApple(idToken: String, rawNonce: String): DataResult<AuthUser> = signedIn()

    override suspend fun sendPasswordReset(email: String): DataResult<Unit> = DataResult.Success(Unit)

    /** Mock mode keeps the persona's name; the rename still reports success so QA flows complete. */
    override suspend fun updateDisplayName(name: String): DataResult<Unit> {
        val current = state.value ?: return DataResult.Success(Unit)
        state.value = current.copy(displayName = name.trim().ifEmpty { current.displayName })
        return DataResult.Success(Unit)
    }

    /** Sign-out is honoured so QA can exercise the guest surfaces too — re-enter mock mode to reset. */
    override suspend fun signOut() { state.value = null }

    override suspend fun deleteAccount(password: String?): DataResult<Unit> {
        state.value = null
        return DataResult.Success(Unit)
    }

    private fun signedIn(): DataResult<AuthUser> {
        state.value = user
        return DataResult.Success(user)
    }
}
